import React, { forwardRef, HTMLAttributes } from 'react'
import classNames from 'classnames'
import MenuIcon from 'transfix-icons/build/MenuIcon'
import { makeStyles, createStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'

interface INavigationToggleButtonProps
  extends Pick<HTMLAttributes<HTMLButtonElement>, 'onClick' | 'className'> {
  /**
   * @default false
   */
  isOpen: boolean
}

type TClassKey = 'root' | 'linkBackground' | 'open' | 'icon'

interface IUseNavigationToggleButtonStylesProps {
  isMpNavigationBarUpdateEnabled: boolean
}

const useNavigationToggleButtonStyles = makeStyles(theme => {
  return createStyles<TClassKey, IUseNavigationToggleButtonStylesProps>({
    root: {
      position: 'relative',
      display: 'grid',
      placeItems: 'center',
      flex: '0 0 auto',
      height: '100%',
      width: 44,
      padding: 0,
      borderColor: 'transparent',
      backgroundColor: 'transparent',
      zIndex: 2,

      '&:focus, &:hover, &:active': {
        borderColor: 'transparent',
        backgroundColor: 'transparent',
      },

      [theme.breakpoints.up('lg')]: {
        /**
         * @note Show this button on desktop when `isMpNavigationBarUpdateEnabled` is on
         */
        display: ({ isMpNavigationBarUpdateEnabled }) => {
          return isMpNavigationBarUpdateEnabled ? 'grid' : 'none'
        },
      },
    },
    linkBackground: {
      display: 'block',
      position: 'absolute',
      width: 25,
      height: 25,
      borderRadius: '50%',
      backgroundColor: brandColors.coolGray8,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 0,
    },
    open: {
      '& $linkBackground': {
        backgroundColor: brandColors.coolGray6,
        width: 35,
        height: 35,
      },
    },
    icon: {
      position: 'relative',
      zIndex: 1,
    },
  })
})

const NavigationToggleButton = forwardRef<HTMLButtonElement, INavigationToggleButtonProps>(
  ({ className: classNameProp, isOpen = false, onClick }, ref) => {
    const isMpNavigationBarUpdateEnabled = useFeatureFlag('mpNavigationBarUpdate')
    const classes = useNavigationToggleButtonStyles({ isMpNavigationBarUpdateEnabled })
    const className = classNames(classes.root, classNameProp, { [classes.open]: isOpen })
    return (
      <button
        aria-label='Toggle Navigation'
        data-test='NavigationToggleButton'
        className={className}
        onClick={onClick}
        ref={ref}>
        <span className={classes.linkBackground} />
        <MenuIcon color='white' size='xlarge' className={classes.icon} />
      </button>
    )
  }
)

export default NavigationToggleButton
